import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SearchBox from "@/components/feature/SearchBox";
import ArticleCard from "@/components/feature/ArticleCard";
import EmptyState from "@/components/feature/EmptyState";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { searchArticles, getHotReviews, sectionLabels } from "@/lib/content";
import type { Section } from "@/types/content";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

type FilterKey = "all" | Section;

const filters: { key: FilterKey; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "news", label: "新闻" },
  { key: "review", label: "评测" },
  { key: "industry", label: "行业动态" },
];

export default function Search() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const keyword = params.get("q") ?? "";
  const [filter, setFilter] = useState<FilterKey>("all");

  useDocumentMeta({
    title: keyword ? `“${keyword}”的搜索结果 - 汽车资讯网` : "全站搜索 - 汽车资讯网",
    description: keyword
      ? `汽车资讯网关于“${keyword}”的搜索结果，涵盖相关新闻、评测与行业动态。`
      : "在汽车资讯网搜索汽车新闻、车型评测与行业动态。",
    keywords: `${keyword},汽车搜索,汽车资讯`,
  });

  const results = useMemo(() => searchArticles(keyword, filter), [keyword, filter]);
  const counts = useMemo(
    () => ({
      all: searchArticles(keyword, "all").length,
      news: searchArticles(keyword, "news").length,
      review: searchArticles(keyword, "review").length,
      industry: searchArticles(keyword, "industry").length,
    }),
    [keyword],
  );

  const recommended = getHotReviews(3);

  return (
    <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-8">
      <Breadcrumb items={[{ label: "搜索结果" }]} />

      <div className="mt-4 bg-background-50 border border-background-200 rounded-lg p-6 md:p-8">
        <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
          全站搜索
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          搜索范围覆盖新闻、评测与行业动态（基于演示数据，支持标题、摘要与标签匹配）。
        </p>
        <div className="mt-5 max-w-2xl">
          <SearchBox initialValue={keyword} size="lg" />
        </div>
      </div>

      {keyword ? (
        <div className="mt-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-background-50 border border-background-200 rounded-lg px-5 py-4">
            <p className="text-sm text-foreground-700">
              关键词
              <strong className="text-primary-700 mx-1">“{keyword}”</strong>
              共找到 <strong className="text-accent-600">{counts.all}</strong> 条结果
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              {filters.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFilter(f.key)}
                  className={`px-3 py-1.5 rounded-full text-xs cursor-pointer whitespace-nowrap transition-colors ${
                    filter === f.key
                      ? "bg-primary-600 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {f.key === "all" ? "全部" : sectionLabels[f.key]}
                  <span className="ml-1 opacity-80">{counts[f.key]}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            {results.length > 0 ? (
              <div className="flex flex-col gap-4">
                {results.map((item) => (
                  <ArticleCard key={item.id} article={item} variant="row" />
                ))}
              </div>
            ) : (
              <EmptyState
                title="没有找到匹配的内容"
                desc="没有找到与当前关键词相关的结果，试试更换关键词，或浏览下方的推荐内容。"
                actionLabel="清空并重新搜索"
                onAction={() => navigate("/search")}
              />
            )}
          </div>
        </div>
      ) : (
        <div className="mt-6">
          <EmptyState
            title="请输入关键词开始搜索"
            desc="例如：新能源、SUV、智能驾驶、电池、价格等，系统会在演示数据中为你匹配相关资讯。"
          />
        </div>
      )}

      <section className="mt-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-1.5 h-7 rounded-full bg-accent-500" />
          <h2 className="font-heading font-bold text-xl text-foreground-950">
            热门推荐
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {recommended.map((item) => (
            <ArticleCard key={item.id} article={item} variant="grid" />
          ))}
        </div>
      </section>

      <p className="mt-8 text-center text-xs text-foreground-400">
        搜索与筛选均为前端演示功能，数据来自本站示例内容，暂未接入真实接口。
      </p>
    </div>
  );
}